import {
  Stack,
  Typography,
  Card,
  CardContent,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Divider,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAlergyIntoleranceByOrganization } from "@/services/fhir/alergyintolerance/hook/useAlergyIntoleranceByOrganization";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";
import { GenericTable } from "@/components/GenericTable/GenericTable";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map((item) => item.label.length));
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);
  return Math.min(calculatedWidth, 400);
};
import SearchIcon from "@mui/icons-material/Search";
import { useDict } from "@/utils/useDict";
import { NoData } from "@/components/common/NoData";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`allergy-tabpanel-${index}`}
      aria-labelledby={`allergy-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface AllergyIntoleranceData {
  id: string;
  clinicalStatus: string;
  clinicalStatusLabel?: string;
  verificationStatus: string;
  type: string;
  category: string;
  criticality: string;
  code: string;
  display: string;
  patientId: string;
  patientName: string;
  recordedDate: string;
}

// Função para formatar data
function formatDate(dateString: string): string {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  } catch {
    return dateString;
  }
}

export function AllergiesAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientAllergies, setPatientAllergies] = useState<
    AllergyIntoleranceData[]
  >([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(
    new Map()
  );
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [topSearch, setTopSearch] = useState<string>("");

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      console.log("Filtros de alergias atualizados:", filters);
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  // Hook para buscar AllergyIntolerances
  const {
    data: allergyData,
    loading: loadingAllergies,
    fetchAlergyIntolerances,
  } = useAlergyIntoleranceByOrganization({
    organizationId: organizationId || "",
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    includePatient: true,
    includeEncounter: true,
  });

  // Processar dados dos AllergyIntolerances
  const {
    allergiesByClinicalStatus,
    allergiesByCategory,
    allergiesByCriticality,
    allergiesByType,
    totalAllergies,
    allergiesList,
    topAllergens,
    patientsSummaryRows,
  } = useMemo(() => {
    const clinicalStatusMap = new Map<string, number>();
    const categoryMap = new Map<string, number>();
    const criticalityMap = new Map<string, number>();
    const typeMap = new Map<string, number>();
    const allergenMap = new Map<string, number>();
    const allergiesList: AllergyIntoleranceData[] = [];
    const patientsMap = new Map<string, fhir5.Patient>();

    // Processar pacientes
    if (allergyData?.patients) {
      allergyData.patients.forEach((patient) => {
        if (patient.id) {
          patientsMap.set(patient.id, patient);
        }
      });
    }

    const allergies = allergyData?.allergyIntolerances || [];

    allergies.forEach((allergy) => {
      // Clinical Status
      const clinicalStatus =
        allergy.clinicalStatus?.coding?.[0]?.code || "unknown";
      clinicalStatusMap.set(
        clinicalStatus,
        (clinicalStatusMap.get(clinicalStatus) || 0) + 1
      );

      // Category
      const category = allergy.category?.[0] || "unknown";
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);

      // Criticality
      const criticality = allergy.criticality || "unknown";
      criticalityMap.set(
        criticality,
        (criticalityMap.get(criticality) || 0) + 1
      );

      // Type - converter para string
      const type = typeof allergy.type === "string" ? allergy.type : "unknown";
      typeMap.set(type, (typeMap.get(type) || 0) + 1);

      // Código/Alérgeno - suporta tanto coding quanto text
      let code = "unknown";
      let display = "Alérgeno não especificado";

      if (allergy.code?.coding?.[0]) {
        code = allergy.code.coding[0].code || "unknown";
        display =
          allergy.code.coding[0].display ||
          allergy.code.text ||
          "Alérgeno não especificado";
      } else if (allergy.code?.text) {
        display = allergy.code.text;
        code = allergy.code.text;
      }

      allergenMap.set(display, (allergenMap.get(display) || 0) + 1);

      // Dados para a lista
      const patientRef =
        allergy.patient?.reference?.replace("Patient/", "") || "";
      const patient = patientsMap.get(patientRef);
      const patientName = patient?.name?.[0]?.text || "Paciente não encontrado";

      // Extrair valores como string
      const clinicalStatusStr =
        typeof allergy.clinicalStatus === "string"
          ? allergy.clinicalStatus
          : allergy.clinicalStatus?.coding?.[0]?.code || "unknown";

      const verificationStatusStr =
        typeof allergy.verificationStatus === "string"
          ? allergy.verificationStatus
          : allergy.verificationStatus?.coding?.[0]?.code || "unknown";

      const typeStr = type;

      allergiesList.push({
        id: allergy.id || "",
        clinicalStatus: clinicalStatusStr,
        clinicalStatusLabel:
          useDict.clinicalStatus[clinicalStatusStr] || clinicalStatusStr,
        verificationStatus: verificationStatusStr,
        type: typeStr,
        category: useDict.category[category] || category,
        criticality: useDict.criticality[criticality] || criticality,
        code: code,
        display: display,
        patientId: allergy.patient?.reference?.replace("Patient/", "") || "",
        patientName: patientName,
        recordedDate: formatDate(allergy.recordedDate || ""),
      });
    });

    setPatients(patientsMap);

    // Agregar por paciente para a aba "Por Paciente"
    const patientsSummaryMap = new Map<
      string,
      {
        patientId: string;
        patientName: string;
        high: number;
        low: number;
        active: number;
        inactive: number;
        resolved: number;
      }
    >();

    for (const item of allergiesList) {
      const key = item.patientId || "";
      if (!key) continue;
      if (!patientsSummaryMap.has(key)) {
        patientsSummaryMap.set(key, {
          patientId: item.patientId,
          patientName: item.patientName,
          high: 0,
          low: 0,
          active: 0,
          inactive: 0,
          resolved: 0,
        });
      }
      const agg = patientsSummaryMap.get(key)!;
      if (item.criticality === "Alto") agg.high += 1;
      if (item.criticality === "Baixo") agg.low += 1;
      if (item.clinicalStatus === "active") agg.active += 1;
      if (item.clinicalStatus === "inactive") agg.inactive += 1;
      if (item.clinicalStatus === "resolved") agg.resolved += 1;
    }

    const patientsSummaryRows = Array.from(patientsSummaryMap.values()).sort(
      (a, b) => a.patientName.localeCompare(b.patientName)
    );

    return {
      allergiesByClinicalStatus: Array.from(clinicalStatusMap.entries()).map(
        ([status, count]) => ({
          status: useDict.clinicalStatus[status] || status,
          count,
          percentage: (count / allergies.length) * 100,
        })
      ),
      allergiesByCategory: Array.from(categoryMap.entries()).map(
        ([category, count]) => ({
          category: useDict.category[category] || category,
          count,
          percentage: (count / allergies.length) * 100,
        })
      ),
      allergiesByCriticality: Array.from(criticalityMap.entries()).map(
        ([criticality, count]) => ({
          criticality: useDict.criticality[criticality] || criticality,
          count,
          percentage: (count / allergies.length) * 100,
        })
      ),
      allergiesByType: Array.from(typeMap.entries()).map(([type, count]) => ({
        type:
          type === "allergy"
            ? "Alergia"
            : type === "intolerance"
            ? "Intolerância"
            : type,
        count,
        percentage: (count / allergies.length) * 100,
      })),
      topAllergens: Array.from(allergenMap.entries())
        .map(([display, count]) => ({ display, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10), // Top 10 alérgenos
      totalAllergies: allergies.length,
      allergiesList,
      patientsSummaryRows,
    };
  }, [allergyData]);

  // Buscar dados quando os filtros mudarem
  useEffect(() => {
    if (organizationId) {
      fetchAlergyIntolerances();
    }
  }, [
    organizationId,
    dateRange,
    selectedLocation,
    selectedPractitioner,
    fetchAlergyIntolerances,
  ]);

  // Filtrar lista agregada por paciente
  const filteredPatientsSummary = useMemo(() => {
    if (!search) return patientsSummaryRows;
    return patientsSummaryRows.filter((row) =>
      row.patientName.toLowerCase().includes(search.toLowerCase())
    );
  }, [patientsSummaryRows, search]);

  // Abrir diálogo do paciente
  const handlePatientClick = (patientId: string) => {
    const patient = patients.get(patientId);
    if (patient) {
      setSelectedPatient(patient);
      const patientAllergyList = allergiesList.filter(
        (allergy) => allergy.patientId === patientId
      );
      setPatientAllergies(patientAllergyList);
      setPatientDialogOpen(true);
    }
  };

  // Filtrar alergias do paciente no diálogo
  const filteredPatientAllergies = useMemo(() => {
    if (!dialogSearch) return patientAllergies;
    return patientAllergies.filter(
      (allergy) =>
        allergy.display.toLowerCase().includes(dialogSearch.toLowerCase()) ||
        allergy.clinicalStatus
          .toLowerCase()
          .includes(dialogSearch.toLowerCase())
    );
  }, [patientAllergies, dialogSearch]);

  const filteredTopAllergens = useMemo(() => {
    return (topAllergens || []).filter((item) =>
      item.display.toLowerCase().includes(topSearch.toLowerCase())
    );
  }, [topAllergens, topSearch]);

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Alergias"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {/* Abas */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Dados Gerais" />
          <Tab label="Por Paciente" />
        </Tabs>
      </Box>

      {/* Conteúdo das Abas */}
      <TabPanel value={currentTab} index={0}>
        <Stack spacing={5}>
          {/* Gráfico de Status Clínico */}
          {allergiesByClinicalStatus.length > 0 ? (
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                {(() => {
                  const sorted = [...allergiesByClinicalStatus].sort(
                    (a, b) => b.count - a.count
                  );
                  const total = allergiesByClinicalStatus.reduce(
                    (acc, item) => acc + item.count,
                    0
                  );
                  const percentage =
                    total > 0 ? Math.round((sorted[0].count / total) * 100) : 0;
                  return `${percentage}% das alergias estão com status '${sorted[0].status}'`;
                })()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                paragraph
                sx={{ fontSize: "0.9rem" }}
              >
                Acompanhe o status das alergias para garantir a segurança do
                paciente. Alergias "ativas" exigem atenção redobrada em
                prescrições e procedimentos.
              </Typography>
              <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={allergiesByClinicalStatus.map((item) => ({
                      label: item.status,
                      value: item.count,
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      dataKey="label"
                      type="category"
                      width={calculateYAxisWidth(
                        allergiesByClinicalStatus.map((item) => ({
                          label: item.status,
                          value: item.count,
                        }))
                      )}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip
                      formatter={(value: number | undefined) => {
                        if (value === undefined) return ["0", "Quantidade"];
                        const total = allergiesByClinicalStatus.reduce(
                          (acc, item) => acc + item.count,
                          0
                        );
                        const percentage =
                          total > 0 ? Math.round((value / total) * 100) : 0;
                        return [`${value} (${percentage}%)`, "Quantidade"];
                      }}
                    />
                    <Bar dataKey="value" name="Quantidade">
                      {allergiesByClinicalStatus.map((item, index) => {
                        const maxValue = Math.max(
                          ...allergiesByClinicalStatus.map((d) => d.count)
                        );
                        const isHighlight = item.count === maxValue;
                        return (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              isHighlight
                                ? theme.palette.primary.main
                                : "#9E9E9E"
                            }
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <NoData message="Sem dados de status clínico." />
          )}

          {/* Gráfico de Criticidade */}
          {allergiesByCriticality.length > 0 && <Divider />}

          {allergiesByCriticality.length > 0 ? (
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                {(() => {
                  const sorted = [...allergiesByCriticality].sort(
                    (a, b) => b.count - a.count
                  );
                  const total = allergiesByCriticality.reduce(
                    (acc, item) => acc + item.count,
                    0
                  );
                  const percentage =
                    total > 0 ? Math.round((sorted[0].count / total) * 100) : 0;
                  return `${percentage}% das alergias têm criticidade '${sorted[0].criticality}'`;
                })()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                paragraph
                sx={{ fontSize: "0.9rem" }}
              >
                Entenda o nível de risco das alergias. Um número alto de
                criticidade "Alta" sinaliza a importância de protocolos de
                emergência e comunicação clara com o paciente.
              </Typography>
              <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={allergiesByCriticality.map((item) => ({
                      label: item.criticality,
                      value: item.count,
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      dataKey="label"
                      type="category"
                      width={calculateYAxisWidth(
                        allergiesByCriticality.map((item) => ({
                          label: item.criticality,
                          value: item.count,
                        }))
                      )}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip
                      formatter={(value: number | undefined) => {
                        if (value === undefined) return ["0", "Quantidade"];
                        const total = allergiesByCriticality.reduce(
                          (acc, item) => acc + item.count,
                          0
                        );
                        const percentage =
                          total > 0 ? Math.round((value / total) * 100) : 0;
                        return [`${value} (${percentage}%)`, "Quantidade"];
                      }}
                    />
                    <Bar dataKey="value" name="Quantidade">
                      {allergiesByCriticality.map((item, index) => {
                        const maxValue = Math.max(
                          ...allergiesByCriticality.map((d) => d.count)
                        );
                        const isHighlight = item.count === maxValue;
                        return (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              isHighlight
                                ? theme.palette.primary.main
                                : "#9E9E9E"
                            }
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <NoData message="Sem dados de criticidade." />
          )}

          {/* Tabela de Top Alérgenos */}
          {topAllergens.length > 0 && <Divider />}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Alergias e Intolerâncias Mais Comuns
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar alérgeno..."
              value={topSearch}
              onChange={(e) => setTopSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 2 }}>
              <GenericTable
                tableRows={filteredTopAllergens.map((item) => ({
                  display: item.display,
                  count: item.count,
                  percentage:
                    totalAllergies > 0
                      ? `${((item.count / totalAllergies) * 100).toFixed(1)}%`
                      : "0%",
                }))}
                tableColumns={[
                  {
                    column: "Alergia/Intolerância",
                    rowName: "display",
                    align: "left",
                  },
                  {
                    column: "Quantidade",
                    rowName: "count",
                    width: "140px",
                  },
                  {
                    column: "% do Total",
                    rowName: "percentage",
                    width: "140px",
                  },
                ]}
                characterLimit={80}
              />
            </Box>
          </Box>
        </Stack>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Stack spacing={2}>
          {/* Busca */}
          <TextField
            fullWidth
            placeholder="Buscar por alérgeno, paciente ou status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Tabela agregada por paciente */}
          {loadingAllergies ? (
            <Skeleton variant="rectangular" height={400} />
          ) : (
            <GenericTable
              tableRows={filteredPatientsSummary}
              tableColumns={[
                { column: "Paciente", rowName: "patientName", align: "left" },
                { column: "Gravidade Alta", rowName: "high", width: "140px" },
                { column: "Gravidade Baixa", rowName: "low", width: "140px" },
                { column: "Ativas", rowName: "active", width: "100px" },
                { column: "Inativas", rowName: "inactive", width: "110px" },
                { column: "Resolvidas", rowName: "resolved", width: "120px" },
              ]}
              characterLimit={60}
              onRowClick={(row) => handlePatientClick(row.patientId)}
            />
          )}
        </Stack>
      </TabPanel>

      {/* Diálogo do Paciente */}
      <Dialog
        open={patientDialogOpen}
        onClose={() => setPatientDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Alergias do Paciente: {selectedPatient?.name?.[0]?.text}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              fullWidth
              placeholder="Buscar alergias..."
              value={dialogSearch}
              onChange={(e) => setDialogSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <GenericTable
              tableRows={filteredPatientAllergies}
              tableColumns={[
                {
                  column: "Alergia/Intolerância",
                  rowName: "display",
                  align: "left",
                },
                {
                  column: "Tipo",
                  rowName: "category",
                },
                {
                  column: "Gravidade",
                  rowName: "criticality",
                },
                {
                  column: "Data",
                  rowName: "recordedDate",
                  width: "120px",
                },
              ]}
              characterLimit={80}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatientDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
